import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders
} from 'axios';
import { Auth } from 'aws-amplify';
import getUserSession from '@utils/getSession';
import { appVersion } from '../../constants/config';
import { Logger } from '../utils/logger';
import { SELECTED_COMPANY_ID } from '@constants/index';
import { getDeviceId } from '../utils/deviceObservability';
import { clearAppStorage } from '../utils/storage';

type DefaultInterceptorOptions = {
  transformAuthToken?: (token: string) => string;
};

const attachDefaultInterceptors = (
  instance: AxiosInstance,
  options?: DefaultInterceptorOptions
) => {
  instance.interceptors.request.use(async (config) => {
    const location = window.location;
    const { token, selectedCompanyId } = await getUserSession();

    const headers = { ...(config.headers ?? {}) } as AxiosRequestHeaders;

    const authToken = options?.transformAuthToken
      ? options.transformAuthToken(token)
      : token;

    headers.Authorization = authToken;
    if (selectedCompanyId) {
      headers['x-company-id'] = selectedCompanyId;
    }
    headers['x-page'] = location.pathname + location.search + location.hash;
    if (appVersion) {
      headers['x-app-version'] = appVersion;
    }
    headers['x-app-client'] = 'desktop';
    headers['x-device-id'] = getDeviceId();
    config.headers = headers;

    return config;
  });

  // Handle auth errors
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        Auth.currentAuthenticatedUser({ bypassCache: true }).catch(() => {
          // The token was rejected and could not be renewed — a real end of
          // session, so the analytics identity goes with it.
          clearAppStorage({ keepAnalyticsIdentity: false });
          window.location.href = '/login';
          Logger.info(
            'client::api::response: 401 error, clearing localStorage and redirecting to login'
          );
        });
      } else if (error.response && error.config?.url) {
        // not a network error
        Logger.apiError(
          `client::api::response: ${error.message} calling ${error.config.url}`,
          error
        );
      }
      const responseData = error.response?.data as
        | { message?: string }
        | undefined;

      if (
        error.response?.status === 403 &&
        responseData?.message === 'FORBIDDEN: Invalid Company'
      ) {
        const { selectedCompanyId } = await getUserSession();
        Logger.error(
          `client::api::response: 403 error, companyId ${selectedCompanyId} might be invalid. Clearing company ID in localStorage`
        );
        localStorage.removeItem(SELECTED_COMPANY_ID); // this reset the company selection
      }
      return Promise.reject(error);
    }
  );
};

export const api = axios.create({
  baseURL: import.meta.env.VITE_APP_ENDPOINT,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

attachDefaultInterceptors(api);

export const createApiClient = (
  config: AxiosRequestConfig,
  options?: DefaultInterceptorOptions
) => {
  const instance = axios.create(config);
  attachDefaultInterceptors(instance, options);
  return instance;
};

export { attachDefaultInterceptors };
